import { useCallback, useId, useRef, useState } from 'react'
import type { JSX, MouseEvent, ReactNode } from 'react'

import { UnstyledButton } from '@buffer-mono/popcorn'

import { useClampMetrics } from './useClampMetrics'

import styles from './ExpandableEngagementText.module.css'

const DEFAULT_LINE_CLAMP = 3

type ExpandableEngagementTextProps = {
  children: ReactNode
  lines?: number
}

/**
 * Keeps long engagement text compact while allowing the complete text to be
 * read in place. The full content stays mounted and the reveal wrapper animates
 * between its measured collapsed and natural heights.
 *
 * Clipping is visual only: the whole text stays in the accessibility tree, so
 * assistive technology reads it without expanding. The toggle therefore carries
 * no `aria-expanded` — nothing is hidden for it to describe.
 */
export const ExpandableEngagementText = ({
  children,
  lines = DEFAULT_LINE_CLAMP,
}: ExpandableEngagementTextProps): JSX.Element => {
  const revealRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const revealId = useId()
  const [expanded, setExpanded] = useState(false)
  const { isClamped, collapsedHeight, fullHeight } = useClampMetrics(
    contentRef,
    lines,
  )

  /**
   * `overflow: hidden` hides scrollbars but still scrolls, so anything that
   * reveals clipped content — focusing a link past the clamp, find-in-page —
   * leaves the box showing the middle of the text with no way to scroll back.
   * A non-zero offset means exactly that happened: expand and restore the top.
   *
   * Wired to both focus and scroll. Blink updates `scrollTop` before it
   * dispatches focus, so the focus path handles it in the same tick and nothing
   * flashes; the scroll path is the backstop for any engine that scrolls after
   * dispatching, and for scrolls no focus change caused.
   */
  const handleClippedScroll = useCallback((): void => {
    const reveal = revealRef.current
    if (!reveal || reveal.scrollTop === 0) return

    reveal.scrollTop = 0
    setExpanded(true)
  }, [])

  // The card wrapper treats a click as "expand the card", so keep the toggle's
  // click to itself.
  const handleToggle = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    setExpanded((value) => !value)
  }, [])

  const revealHeight = !isClamped
    ? undefined
    : expanded
      ? fullHeight
      : collapsedHeight

  return (
    <div className={styles.container}>
      <div
        id={revealId}
        ref={revealRef}
        className={styles.reveal}
        style={revealHeight != null ? { height: revealHeight } : undefined}
        onFocus={handleClippedScroll}
        onScroll={handleClippedScroll}
      >
        <div ref={contentRef} className={styles.content}>
          {children}
        </div>
      </div>
      {isClamped && (
        <UnstyledButton
          type="button"
          className={styles.toggle}
          onClick={handleToggle}
          aria-controls={revealId}
        >
          {expanded ? 'Show less' : 'See more'}
        </UnstyledButton>
      )}
    </div>
  )
}
